import { Component, ErrorInfo, ReactNode } from "react";
import { Warning, ArrowsClockwise, House } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { captureError } from "@/lib/sentry";
import i18n from "@/i18n";

interface Props {
  children: ReactNode;
  routeName: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Route-level error boundary for graceful error handling per page/route.
 * Unlike the global ErrorBoundary, this allows other routes to continue working
 * if one route crashes.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console in development
    console.error(
      `RouteErrorBoundary caught error in ${this.props.routeName}:`,
      error,
      errorInfo,
    );

    // Send to Sentry for production error tracking
    captureError(error, {
      componentStack: errorInfo.componentStack,
      routeName: this.props.routeName,
      routeErrorBoundary: true,
    });
  }

  handleTryAgain = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = (): void => {
    window.location.href = "/dashboard";
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center">
              <Warning size={28} className="text-destructive" />
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                {i18n.t("routeErrorBoundary.title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {i18n.t("routeErrorBoundary.description")}
              </p>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="text-left bg-muted/50 rounded-lg p-3 overflow-auto max-h-32">
                <p className="text-xs font-mono text-destructive break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button
                onClick={this.handleTryAgain}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <ArrowsClockwise size={14} />
                {i18n.t("routeErrorBoundary.tryAgain")}
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="default"
                size="sm"
                className="gap-2"
              >
                <House size={14} />
                {i18n.t("routeErrorBoundary.dashboard")}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
